import type { ComputedRef, Ref } from 'vue'
import { computed, toValue } from 'vue'
import { assertNever } from '@/helpers'
import type {
    NonSkuGemstoneImageFragment,
    NonSkuLabGrownStoneImageFragment,
    StoneFragment,
} from '@/graphql/api'
import type { ImageProps } from '@/components/Image.vue'
import { getGenericImageProps } from '@/components/useImageProps.ts'

export type StoneImage = { sample: boolean } & (
    | {
          url: string
      }
    | { image: ImageProps }
)

export function getStoneImage(stone: StoneFragment): StoneImage | undefined {
    if (stone.image?.url) {
        return {
            url: stone.image.url,
            sample: false,
        }
    }

    if (stone.shapeWithInfo.info?.samplePhoto) {
        return {
            image: getGenericImageProps({
                size: stone.shapeWithInfo.info.samplePhoto.size,
                size_x2: stone.shapeWithInfo.info.samplePhoto.size_x2,
            }),
            sample: true,
        }
    }

    return undefined
}

type StoneImageTypes =
    | NonSkuLabGrownStoneImageFragment
    | NonSkuGemstoneImageFragment
    | StoneFragment

export function useStoneImage(
    stone:
        | Ref<StoneImageTypes | null | undefined>
        | (() => StoneImageTypes | null | undefined)
): ComputedRef<StoneImage | undefined> {
    return computed(() => {
        const st = toValue(stone)

        if (!st) {
            return undefined
        }

        if ('GemsApi_Stone' === st.__typename) {
            return getStoneImage(st)
        }

        if ('NonSkuGemstone' === st.__typename) {
            if (st.image) {
                return {
                    image: getGenericImageProps({
                        size: st.image.size,
                        size_x2: st.image.size_x2,
                    }),
                    sample: false,
                }
            }
            if (st.shapeDimension.shape.samplePhoto) {
                return {
                    image: getGenericImageProps({
                        size: st.shapeDimension.shape.samplePhoto.size,
                        size_x2: st.shapeDimension.shape.samplePhoto.size_x2,
                    }),
                    sample: true,
                }
            }

            return undefined
        }

        if ('NonSkuLabGrown' === st.__typename) {
            if (st.image) {
                return {
                    image: getGenericImageProps({
                        size: st.image.size,
                        size_x2: st.image.size_x2,
                    }),
                    sample: false,
                }
            }
            if (st.shapeDimension.image) {
                return {
                    image: getGenericImageProps({
                        size: st.shapeDimension.image.size,
                        size_x2: st.shapeDimension.image.size_x2,
                    }),
                    sample: true,
                }
            }
            if (st.shapeDimension.shape.samplePhoto) {
                return {
                    image: getGenericImageProps({
                        size: st.shapeDimension.shape.samplePhoto.size,
                        size_x2: st.shapeDimension.shape.samplePhoto.size_x2,
                    }),
                    sample: true,
                }
            }

            return undefined
        }

        return assertNever(st, undefined)
    })
}
